import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { inject, observer } from 'mobx-react'
import { useEffect } from 'react'

import PlaidLink from './components/PlaidLink'
import { withRouterProps } from '../../../utils'
import { HelpIcon, Spacer, Text, TooltipText, WizardStep } from '../../../components'
import { InvestmentChoice, InvestmentChoiceRow } from './AddInvestmentsStyle'
import { accountTypeKeyToPlanTypeId } from '../../../constants'
import { MOBILE } from '../../../guided-toolbox/utils/breakpoints'
import { useStatementUpload } from './upload'

const SkipLink = styled.button`
  display: block;
  /* Matches the design's spacing below the button row. */
  margin: 50px auto 0;
  border: none;
  background: none;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  cursor: pointer;
  text-decoration: underline;

  ${MOBILE} {
    margin-top: 20px;
  }
`

const employerRelated = {
  '401a': true,
  '401k': true,
  '403b': true,
  '457': true,
  'keogh': true,
  'ira-rollover': false,
  'ira-roth': false,
  'ira-deductible': false,
  'ira-non-deductible': false,
  'brokerage-account': false,
  'other-asset': false,
  'annuity-fixed': false,
  'payroll-deduct-ira': false,
  'sep-ira': false,
  'simple-ira': false,
}

const informationEntryContent = ({ config }) => {
  const intro = 'Follow the steps to enter information from the most recent account statement.'

  switch (config.templateId) {
    // Florida template
    case 1:
    case 2:
      return `${intro} If you need help, contact the ${config.template.supportGroup} via the ${config.template.supportName} at ${config.template.supportLine} or on ${config.template.supportLinkOverDomain}`

    // Every other template, retail included: supportName isn't populated, so name
    // customer service the way the disclosure copy does instead of interpolating it.
    default:
      return `${intro} If you need help, contact GuidedChoice Customer Service at ${config.template.supportLine}.`
  }
}

const AddInvestments = props => {
  const {
    params: { type, accountType, id },
  } = props.match
  const {
    features,
    getFeatures,
    config,
    config: { setRetirementAccountReviewStatus },
    institutionalAccountUnderReview,
    institutionalAccount,
  } = props.store
  const isInstitutionalAccount = props.account?.account?.id === institutionalAccount?.id
  const { openUpload } = useStatementUpload({
    store: props.store,
    type,
    accountType,
    accountId: id,
  })

  useEffect(() => {
    const fetchFeatures = async () => {
      if (isInstitutionalAccount && institutionalAccountUnderReview) {
        await setRetirementAccountReviewStatus()
      }
      await getFeatures()
    }

    return () => fetchFeatures()
  }, [
    getFeatures,
    institutionalAccountUnderReview,
    isInstitutionalAccount,
    setRetirementAccountReviewStatus,
  ])

  const handleBack = () => {
    props.navigate(-1)
  }

  const handleNext = () => {
    props.navigate(`/accounts/${type}/${accountType}/${id}`)
  }

  const handleAddInvestments = () => {
    props.navigate(`/accounts/add/${type}/${accountType}/${id}/investments`)
  }

  const handlePlaidLinkSuccess = () => {
    props.store.getExistingPlaidLinks()
    props.store.getAccounts()
    handleAddInvestments()
  }

  const linkingAvailable =
    (features.enablePlaidLink && type === 'non-gc') ||
    (features.enablePlaidLink && type === 'Institutional' && features.editInstitutionalAccount)

  // The variant is passed in by each layout rather than derived from `linkingAvailable`: only the
  // layout knows whether the gray treatment belongs to this button or to the skip button.
  const manuallyAddButton = variant => (
    <InvestmentChoice
      label='Manually add'
      sublabel='manually add investments'
      variant={variant}
      onClick={handleAddInvestments}
    />
  )

  const uploadStatementButton = (
    <InvestmentChoice
      label='Upload a statement'
      sublabel='let AI enter the numbers'
      onClick={openUpload}
    />
  )

  return (
    <WizardStep
      onBackClick={handleBack}
      onNextClick={handleNext}
      showDefaultButtons={false}
      title="Let's talk investments">
      <Text>We need information about how this account is invested.</Text>

      <Spacer space='12px' />

      <Text
        css={css`
          margin-bottom: 24px;
        `}>
        {informationEntryContent({ config })}
      </Text>

      {employerRelated[accountType] && (
        <Text>
          Important: We can provide more personalized investment advice if you include all the
          investment options available — even those without a balance. These investment options must
          be entered manually. <br />
          <HelpIcon tooltip={TooltipText.otherInvestmentOptions()} />
        </Text>
      )}

      <Spacer space='24px' />

      {linkingAvailable ? (
        <>
          <InvestmentChoiceRow>
            {/* Four choices, so skipping drops to a text link and the gray treatment lands here. */}
            {manuallyAddButton('tertiaryLight')}
            {uploadStatementButton}
            <PlaidLink
              onSuccess={handlePlaidLinkSuccess}
              accountId={id}
              bigButton
              planType={accountTypeKeyToPlanTypeId[accountType]}
            />
          </InvestmentChoiceRow>
          <SkipLink type='button' onClick={handleNext}>
            Skip investments for now
          </SkipLink>
        </>
      ) : (
        <InvestmentChoiceRow>
          <InvestmentChoice
            label='Skip investments'
            sublabel={
              <>
                I'll come back or have input
                <br />
                done for me
              </>
            }
            variant='tertiaryLight'
            onClick={handleNext}
          />
          {/* Skipping is a button here, so it takes the gray treatment instead. */}
          {manuallyAddButton('primary')}
          {uploadStatementButton}
        </InvestmentChoiceRow>
      )}
    </WizardStep>
  )
}

export default withRouterProps(inject('store', 'account')(observer(AddInvestments)))
